import { useState } from 'react';
import { T, useGT } from 'gt-react';
import {
  CopySimpleIcon,
  PauseIcon,
  PencilSimpleIcon,
  PlayIcon,
  SpinnerGapIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { Action, Subject } from '@erp/authz';
import { ValidationRuleStatus } from '@erp/shared-types';
import type { ValidationRuleDTO } from '@erp/rpc';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { Can } from '@/hooks/useCan';
import {
  useDeleteValidationRule,
  useDuplicateValidationRule,
  useRunValidationRuleNow,
  useUpdateValidationRule,
} from '@/api/rpc/hooks/inventory-management/useValidationRules';

export interface ValidationRuleActionsPanelProps {
  rule: ValidationRuleDTO;
  /** Opens the rule builder on `rule` (edit) or on a fresh duplicate. */
  onEdit: (rule: ValidationRuleDTO) => void;
  /** Where the page goes once the rule is gone. */
  onDeleted: () => void;
}

/**
 * The right rail of a rule's page: everything that changes the rule rather than
 * describing it.
 *
 * Activating and pausing live here rather than in the builder — the builder
 * deliberately leaves a saved rule's status alone, so this is the only place a
 * live rule is switched off. Running now is confirmed first: it writes
 * violations and can stage a review, which is not what a stray click should do.
 */
export function ValidationRuleActionsPanel({
  rule,
  onEdit,
  onDeleted,
}: ValidationRuleActionsPanelProps) {
  const gt = useGT();
  const updateRule = useUpdateValidationRule();
  const duplicateRule = useDuplicateValidationRule();
  const runNow = useRunValidationRuleNow();
  const deleteRule = useDeleteValidationRule(onDeleted);

  const [runOpen, setRunOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const isActive = rule.status === ValidationRuleStatus.Active;
  // A paused rule is refused by the server rather than run and recorded.
  const canRun = rule.status !== ValidationRuleStatus.Paused;

  function handleStatusToggle() {
    updateRule.mutate({
      id: rule.id,
      status: isActive
        ? ValidationRuleStatus.Paused
        : ValidationRuleStatus.Active,
    });
  }

  async function handleDuplicate() {
    try {
      const result = await duplicateRule.mutateAsync({ id: rule.id });
      onEdit(result.rule);
    } catch {
      // The mutation's own toast reports it — there is no copy to open.
    }
  }

  function handleRunNow() {
    runNow.mutate({ ruleId: rule.id });
    setRunOpen(false);
  }

  return (
    <aside className="w-full shrink-0 space-y-4 lg:w-64">
      <section className="rounded-lg border">
        <h2 className="border-b px-3 py-2 text-sm font-medium">
          <T context="heading of the actions panel on a validation rule's page">
            Actions
          </T>
        </h2>
        <div className="space-y-2 p-3">
          <Can I={Action.Update} a={Subject.Product}>
            <Button
              variant="outline"
              className="w-full justify-start"
              disabled={!canRun || runNow.isPending}
              onClick={() => setRunOpen(true)}
            >
              {runNow.isPending ? (
                <SpinnerGapIcon className="h-4 w-4 animate-spin" />
              ) : (
                <PlayIcon className="h-4 w-4" />
              )}
              <T context="button that evaluates a validation rule now, recording the run">
                Run now
              </T>
            </Button>

            <Button
              variant="outline"
              className="w-full justify-start"
              disabled={updateRule.isPending}
              onClick={handleStatusToggle}
            >
              {isActive ? (
                <PauseIcon className="h-4 w-4" />
              ) : (
                <PlayIcon className="h-4 w-4" />
              )}
              {isActive ? (
                <T context="button that pauses an active validation rule">
                  Pause
                </T>
              ) : (
                <T context="button that makes a draft or paused validation rule live">
                  Activate
                </T>
              )}
            </Button>

            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => onEdit(rule)}
            >
              <PencilSimpleIcon className="h-4 w-4" />
              <T context="button that opens a validation rule in the builder">
                Edit
              </T>
            </Button>

            <Button
              variant="outline"
              className="w-full justify-start"
              disabled={duplicateRule.isPending}
              onClick={() => void handleDuplicate()}
            >
              <CopySimpleIcon className="h-4 w-4" />
              <T context="button that copies a validation rule as a new draft">
                Duplicate
              </T>
            </Button>
          </Can>

          <Can I={Action.Delete} a={Subject.Product}>
            <Button
              variant="outline"
              className="w-full justify-start text-red-600 hover:bg-red-50 hover:text-red-700"
              onClick={() => setDeleteOpen(true)}
            >
              <TrashIcon className="h-4 w-4" />
              <T context="button that deletes a validation rule">Delete</T>
            </Button>
          </Can>
        </div>

        {rule.status === ValidationRuleStatus.Paused && (
          <p className="border-t px-3 py-2 text-xs text-muted-foreground">
            <T context="explains why Run now is unavailable on a paused validation rule">
              A paused rule cannot be run. Activate it first.
            </T>
          </p>
        )}
      </section>

      <ConfirmDialog
        open={runOpen}
        onClose={() => setRunOpen(false)}
        onConfirm={handleRunNow}
        title={
          <T context="title of the confirmation before running a validation rule">
            Run this rule now?
          </T>
        }
        message={gt(
          'It reads every division’s copy of the table, records the run, and updates this rule’s violations. Proposed corrections are staged into a review for someone to approve — nothing is written to the catalog.',
          {
            context:
              'body of the confirmation before running a validation rule',
          },
        )}
        confirmText={gt('Run now', {
          context: 'confirm button of the run-a-validation-rule dialog',
        })}
        cancelText={gt('Cancel', { context: 'cancel button' })}
        loading={runNow.isPending}
      />

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <T context="title of the delete-a-validation-rule confirmation">
                Delete this rule?
              </T>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <T context="body of the delete-a-validation-rule confirmation">
                This removes the rule, its run history, and its violations.
                Reviews the rule already staged are left alone. This cannot be
                undone.
              </T>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteRule.isPending}>
              <T context="cancel button">Cancel</T>
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteRule.isPending}
              onClick={(event) => {
                event.preventDefault();
                deleteRule.mutate({ id: rule.id });
              }}
            >
              <T context="delete confirm button">Delete</T>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </aside>
  );
}
